/**
 * Layout Registry
 *
 * Central registry mapping panel modes to layout strategies.
 * To add a new layout mode, simply register it here.
 */

import {type MediaBrowserPanelConfig} from '../common';
import {CompareLayout} from './CompareLayout';
import {GalleryLayout} from './GalleryLayout';
import {GridLayout} from './GridLayout';
import {type LayoutStrategy} from './LayoutStrategy';

/**
 * Registry of all available layout strategies.
 */
const LAYOUT_STRATEGIES = new Map<string, LayoutStrategy>([
  ['gallery', GalleryLayout],
  ['grid', GridLayout],
  ['compare', CompareLayout],
]);

/**
 * Gets the layout strategy for a given panel configuration.
 *
 * Defaults to GalleryLayout for undefined modes or unknown modes,
 * which provides backward compatibility with existing panels.
 *
 * @param config - Panel configuration
 * @returns Layout strategy for the specified mode
 */
export function getLayoutStrategy(
  config: MediaBrowserPanelConfig
): LayoutStrategy {
  const mode = config.mode || 'gallery';
  // Always return GalleryLayout for invalid, null, or undefined modes
  return LAYOUT_STRATEGIES.get(mode) || GalleryLayout;
}

/**
 * Registers a new layout strategy.
 *
 * Use this to add custom layout modes at runtime.
 *
 * @param id - Unique identifier for the layout
 * @param strategy - Layout strategy implementation
 */
export function registerLayoutStrategy(
  id: string,
  strategy: LayoutStrategy
): void {
  LAYOUT_STRATEGIES.set(id, strategy);
}

/**
 * Gets all registered layout strategy IDs.
 *
 * @returns Array of layout strategy IDs
 */
export function getRegisteredLayoutIds(): string[] {
  return Array.from(LAYOUT_STRATEGIES.keys());
}
